import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EmailService } from './service';
import { ErrorToast } from '../../shared/toasters/toasters';

interface EmailDetail {
  id: number;
  type: string;
  status: string;
  subject: string;
  content: string;
  errors: string | null;
  sendAt: string | null;
  sender: string;
  receiver: string;
  createdAt: string;
  updatedAt: string;
}

const statusBadgeVariant = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SENT':       return 'success';
    case 'FAILED':     return 'danger';
    case 'READY':      return 'warning';
    case 'PROCESSING': return 'info';
    default:           return 'secondary';
  }
};

const EmailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState<EmailDetail | null>(null);

  useEffect(() => {
    if (id) {
      EmailService.getEmail(Number(id))
        .then((res: any) => setEmail(res.data ?? res))
        .catch(ErrorToast);
    }
  }, [id]);

  if (!email) {
    return (
      <Col>
        <Card>
          <CardBody>{t('loading')}</CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="d-flex align-items-center mb-3">
              <Button variant="secondary" size="sm" onClick={() => navigate('/emails')}>
                <i className="ti ti-arrow-left me-1"></i>{t('email.backToList')}
              </Button>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <tbody>
                  <tr>
                    <th style={{ width: '25%' }}>{t('email.id')}</th>
                    <td>{email.id}</td>
                  </tr>
                  <tr>
                    <th>{t('email.type')}</th>
                    <td>{email.type}</td>
                  </tr>
                  <tr>
                    <th>{t('email.status')}</th>
                    <td>
                      <Badge bg={statusBadgeVariant(email.status)}>{email.status}</Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>{t('email.subject')}</th>
                    <td>{email.subject}</td>
                  </tr>
                  <tr>
                    <th>{t('email.sender')}</th>
                    <td>{email.sender}</td>
                  </tr>
                  <tr>
                    <th>{t('email.receiver')}</th>
                    <td>{email.receiver}</td>
                  </tr>
                  <tr>
                    <th>{t('email.sendAt')}</th>
                    <td>{email.sendAt ? new Date(email.sendAt).toLocaleString() : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>{t('email.createdAt')}</th>
                    <td>{new Date(email.createdAt).toLocaleString()}</td>
                  </tr>
                  {email.errors && (
                    <tr>
                      <th>{t('email.errors')}</th>
                      <td className="text-danger" style={{ whiteSpace: 'pre-wrap' }}>{email.errors}</td>
                    </tr>
                  )}
                  <tr>
                    <th>{t('email.content')}</th>
                    <td>
                      <div
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          border: '1px solid #dee2e6',
                          padding: '12px',
                          borderRadius: '4px',
                        }}
                        dangerouslySetInnerHTML={{ __html: email.content }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default EmailView;

